import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, Loader2, Flag, Undo2, Target,
  Plus, Minus
} from 'lucide-react';


export default function FanInGame() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(1);
  const [lastEvent, setLastEvent] = useState(null);

  useEffect(() => {
    fetchGame();
    fetchEvents();
  }, [gameId]);

  const fetchGame = async () => {
    try {
      const response = await api.get(`/fan/games/${gameId}`);
      setGame(response.data);
      setPeriod(response.data.current_period || 1);
    } catch (error) {
      toast.error('Failed to load game');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get(`/fan/games/${gameId}/events`);
      setEvents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const addEvent = async (eventType, subType = null, points = 0) => {
    try {
      const response = await api.post(`/fan/games/${gameId}/events`, {
        event_type: eventType,
        sub_type: subType,
        period: period,
        points: points
      });
      
      setGame(response.data.game);
      setLastEvent(response.data.event);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to log stat');
    }
  };

  const undoLastEvent = async () => {
    if (!lastEvent) return;
    
    try {
      await api.delete(`/fan/games/${gameId}/events/${lastEvent.event_id}`);
      setLastEvent(null);
      fetchGame();
      fetchEvents();
      toast.success('Undone');
    } catch (error) {
      toast.error('Failed to undo');
    }
  };

  const updatePeriod = async (newPeriod) => {
    setPeriod(newPeriod);
    try {
      await api.patch(`/fan/games/${gameId}`, {
        current_period: newPeriod
      });
    } catch (error) {
      console.error('Failed to update period:', error);
    }
  };

  const adjustOpponentScore = async (delta) => {
    const scoreField = game.is_home ? 'away_score' : 'home_score';
    const newScore = Math.max(0, (game[scoreField] || 0) + delta);
    
    try {
      const response = await api.patch(`/fan/games/${gameId}`, {
        [scoreField]: newScore
      });
      setGame(response.data);
    } catch (error) {
      toast.error('Failed to update score');
    }
  };

  const adjustPlayerScore = async (delta) => {
    const scoreField = game.is_home ? 'home_score' : 'away_score';
    const newScore = Math.max(0, (game[scoreField] || 0) + delta);
    
    try {
      const response = await api.patch(`/fan/games/${gameId}`, {
        [scoreField]: newScore
      });
      setGame(response.data);
    } catch (error) {
      toast.error('Failed to update score');
    }
  };

  const endGame = async () => {
    if (!confirm('End this game? You can still view the summary afterwards.')) return;
    
    try {
      await api.post(`/fan/games/${gameId}/end`, {});
      toast.success('Game ended!');
      navigate(`/fan/game/${gameId}/summary`);
    } catch (error) {
      toast.error('Failed to end game');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!game) return null;

  const playerName = game.player?.name || 'Player';
  const playerTeam = game.player?.team_name || 'Team';
  const playerJersey = game.player?.jersey_number || '?';
  const playerScore = game.is_home ? game.home_score : game.away_score;
  const opponentScore = game.is_home ? game.away_score : game.home_score;

  // Calculate current stats from events
  const stats = calculateStats(events);

  return (
    <div
      className="h-full overflow-y-auto bg-slate-50 pb-app-nav"
      style={{ WebkitOverflowScrolling: 'touch', paddingBottom: 'calc(env(safe-area-inset-bottom) + 96px)' }}
    >
      {/* Header */}
      <div className="p-3" style={{ backgroundColor: '#13314b', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 text-white/60 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          <div className="text-center">
            <p className="text-xs text-white/50 uppercase tracking-wide">Tracking</p>
            <p className="font-bold text-white">#{playerJersey} {playerName}</p>
          </div>
          
          <Button
            onClick={endGame}
            size="sm"
            data-testid="end-game-button"
            className="font-bold hover:opacity-90"
            style={{ backgroundColor: '#ffa133', color: '#13314b' }}
          >
            <Flag className="w-4 h-4 mr-1" />
            End
          </Button>
        </div>
      </div>

      {/* Scoreboard */}
      <div className="p-4" style={{ backgroundColor: '#13314b' }}>
        <div className="flex items-center justify-between max-w-md mx-auto">
          <div className="text-center">
            <p className="text-xs text-white/60 uppercase">{playerTeam}</p>
            <p className="text-4xl font-bold text-white">{playerScore}</p>
            <div className="flex gap-1 mt-1 justify-center">
              <button
                onClick={() => adjustPlayerScore(-1)}
                data-testid="player-score-minus"
                className="w-6 h-6 rounded text-white/60 hover:text-white"
                style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              >
                <Minus className="w-4 h-4 mx-auto" />
              </button>
              <button
                onClick={() => adjustPlayerScore(1)}
                data-testid="player-score-plus"
                className="w-6 h-6 rounded text-white/60 hover:text-white"
                style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              >
                <Plus className="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-white/60">Q{period}</p>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 4].map((q) => (
                <button
                  key={q}
                  onClick={() => updatePeriod(q)}
                  className="w-6 h-6 rounded text-xs font-bold"
                  style={period === q 
                    ? { backgroundColor: '#ffa133', color: '#13314b' }
                    : { backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }
                  }
                >
                  {q}
                </button>
              ))}
              <button
                onClick={() => updatePeriod(5)}
                className="w-8 h-6 rounded text-xs font-bold"
                style={period === 5
                  ? { backgroundColor: '#ffa133', color: '#13314b' }
                  : { backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }
                }
              >
                OT
              </button>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-xs text-white/60 uppercase">{game.opponent_name}</p>
            <p className="text-4xl font-bold text-white">{opponentScore}</p>
            <div className="flex gap-1 mt-1 justify-center">
              <button
                onClick={() => adjustOpponentScore(-1)}
                data-testid="opponent-score-minus"
                className="w-6 h-6 rounded text-white/60 hover:text-white"
                style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              >
                <Minus className="w-4 h-4 mx-auto" />
              </button>
              <button
                onClick={() => adjustOpponentScore(1)}
                data-testid="opponent-score-plus"
                className="w-6 h-6 rounded text-white/60 hover:text-white"
                style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              >
                <Plus className="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Current Stats */}
      <div className="p-3" style={{ backgroundColor: '#13314b', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex justify-center gap-4 text-center">
          <StatMini label="PTS" value={stats.pts} />
          <StatMini label="REB" value={stats.reb} />
          <StatMini label="AST" value={stats.ast} />
          <StatMini label="STL" value={stats.stl} />
          <StatMini label="BLK" value={stats.blk} />
          <StatMini label="TO" value={stats.to} negative />
        </div>
      </div>

      {/* Stat Buttons */}
      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Scoring */}
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-2 text-center">Scoring</p>
          <div className="grid grid-cols-3 gap-2">
            <StatButton 
              label="2PT Make" 
              onClick={() => addEvent('shot_make', '2pt', 2)}
              color="hover:opacity-90"
              bgColor="#16A085"
              testId="2pt-make-button"
            />
            <StatButton 
              label="3PT Make" 
              onClick={() => addEvent('shot_make', '3pt', 3)}
              color="hover:opacity-90"
              bgColor="#16A085"
              testId="3pt-make-button"
            />
            <StatButton 
              label="FT Make" 
              onClick={() => addEvent('shot_make', 'ft', 1)}
              color="hover:opacity-90"
              bgColor="#16A085"
              testId="ft-make-button"
            />
            <StatButton 
              label="2PT Miss" 
              onClick={() => addEvent('shot_miss', '2pt', 0)}
              color="hover:opacity-90"
              bgColor="#C0392B"
              testId="2pt-miss-button"
            />
            <StatButton 
              label="3PT Miss" 
              onClick={() => addEvent('shot_miss', '3pt', 0)}
              color="hover:opacity-90"
              bgColor="#C0392B"
              testId="3pt-miss-button"
            />
            <StatButton 
              label="FT Miss" 
              onClick={() => addEvent('shot_miss', 'ft', 0)}
              color="hover:opacity-90"
              bgColor="#C0392B"
              testId="ft-miss-button"
            />
          </div>
        </div>

        {/* Rebounds */}
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-2 text-center">Rebounds</p>
          <div className="grid grid-cols-2 gap-2">
            <StatButton 
              label="Off Rebound" 
              onClick={() => addEvent('rebound', 'offensive', 0)}
              color="hover:opacity-90"
              bgColor="#13314b"
              testId="oreb-button"
            />
            <StatButton 
              label="Def Rebound" 
              onClick={() => addEvent('rebound', 'defensive', 0)}
              color="hover:opacity-90"
              bgColor="#13314b"
              testId="dreb-button"
            />
          </div>
        </div>

        {/* Other Stats */}
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-2 text-center">Other</p>
          <div className="grid grid-cols-3 gap-2">
            <StatButton 
              label="Assist" 
              onClick={() => addEvent('assist', null, 0)}
              color="hover:opacity-90"
              bgColor="#13314b"
              testId="assist-button"
            />
            <StatButton 
              label="Steal" 
              onClick={() => addEvent('steal', null, 0)}
              color="hover:opacity-90"
              bgColor="#13314b"
              testId="steal-button"
            />
            <StatButton 
              label="Block" 
              onClick={() => addEvent('block', null, 0)}
              color="hover:opacity-90"
              bgColor="#13314b"
              testId="block-button"
            />
            <StatButton 
              label="Deflection" 
              onClick={() => addEvent('deflection', null, 0)}
              color="hover:opacity-90"
              bgColor="#13314b"
              testId="deflection-button"
            />
            <StatButton 
              label="Turnover" 
              onClick={() => addEvent('turnover', null, 0)}
              color="hover:opacity-90"
              bgColor="#ffa133"
              textColor="#13314b"
              testId="turnover-button"
            />
            <StatButton 
              label="Foul" 
              onClick={() => addEvent('foul', null, 0)}
              color="hover:opacity-90"
              bgColor="#ffa133"
              textColor="#13314b"
              testId="foul-button"
            />
          </div>
        </div>

        {/* Undo */}
        {lastEvent && (
          <div className="text-center pt-2">
            <Button
              onClick={undoLastEvent}
              variant="outline"
              size="sm"
              data-testid="undo-button"
              className="border-slate-300 text-slate-600 hover:bg-slate-100"
            >
              <Undo2 className="w-4 h-4 mr-2" />
              Undo Last
            </Button>
          </div>
        )}
      </div>

      {/* Recent Events Log */}
      <div className="p-4 max-w-lg mx-auto">
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Recent Events</p>
        <div className="rounded-lg divide-y divide-slate-200 max-h-40 overflow-y-auto bg-white border border-slate-200">
          {events.slice(-10).reverse().map((event, idx) => (
            <div key={event.event_id || idx} className="p-2 text-sm flex justify-between">
              <span className="text-slate-600">
                Q{event.period}: {formatEventType(event.event_type, event.sub_type)}
              </span>
              {event.points > 0 && (
                <span className="text-green-400">+{event.points}</span>
              )}
            </div>
          ))}
          {events.length === 0 && (
            <div className="p-4 text-center text-slate-500 text-sm">
              No events yet - start tracking!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatMini({ label, value, negative }) {
  return (
    <div>
      <p className={`text-lg font-bold ${negative ? 'text-orange-400' : 'text-white'}`}>
        {value}
      </p>
      <p className="text-xs text-slate-400">{label}</p>
    </div>
  );
}

function StatButton({ label, onClick, color, bgColor, textColor, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`${color} font-bold py-4 px-2 rounded-lg text-sm transition-all active:scale-95`}
      style={{ backgroundColor: bgColor, color: textColor || '#FFFFFF' }}
    >
      {label}
    </button>
  );
}

function calculateStats(events) {
  const stats = { pts: 0, reb: 0, ast: 0, stl: 0, blk: 0, to: 0, pf: 0 };
  
  for (const event of events) {
    if (event.event_type === 'shot_make') {
      stats.pts += event.points || 0;
    } else if (event.event_type === 'rebound') {
      stats.reb += 1;
    } else if (event.event_type === 'assist') {
      stats.ast += 1;
    } else if (event.event_type === 'steal') {
      stats.stl += 1;
    } else if (event.event_type === 'block') {
      stats.blk += 1;
    } else if (event.event_type === 'turnover') {
      stats.to += 1;
    } else if (event.event_type === 'foul') {
      stats.pf += 1;
    }
  }
  
  return stats;
}

function formatEventType(type, subType) {
  const labels = {
    shot_make: subType ? `${subType.toUpperCase()} Made` : 'Shot Made',
    shot_miss: subType ? `${subType.toUpperCase()} Missed` : 'Shot Missed',
    rebound: subType === 'offensive' ? 'Off Rebound' : 'Def Rebound',
    assist: 'Assist',
    steal: 'Steal',
    block: 'Block',
    deflection: 'Deflection',
    turnover: 'Turnover',
    foul: 'Foul'
  };
  return labels[type] || type;
}
